import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Area, ComposedChart, ScatterChart, Scatter } from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, Target, Calendar, Minus } from 'lucide-react';
import { Metric, MetricForecast } from '../../types';
import { apiService } from '../../services/api';

interface MetricForecastChartProps {
  metric: Metric;
}

export const MetricForecastChart: React.FC<MetricForecastChartProps> = ({ metric }) => {
  const [values, setValues] = useState<any[]>([]);
  const [forecast, setForecast] = useState<MetricForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [metric.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch historical values
      const valuesResponse = await apiService.getMetricValues(metric.id, { limit: 30 });
      
      // Fetch forecast
      const forecastResponse = await apiService.getMetricForecast(metric.id);
      
      // Transform data for charts
      const chartData = valuesResponse.values
        .sort((a, b) => new Date(a.recordedDate).getTime() - new Date(b.recordedDate).getTime())
        .map((value, index) => ({
          date: value.recordedDate,
          value: value.value,
          displayDate: new Date(value.recordedDate).toLocaleDateString(),
          dayNumber: index + 1
        }));

      setValues(chartData);
      setForecast(forecastResponse.forecast);
    } catch (err) {
      console.error('Failed to fetch forecast data:', err);
      setError('Failed to load forecast data');
    } finally {
      setLoading(false);
    }
  };

  const generateForecastData = () => {
    if (!forecast || !values.length) return [];
    
    const lastValue = values[values.length - 1];
    const daysToTarget = metric.targetDate ? 
      Math.ceil((new Date(metric.targetDate).getTime() - new Date(lastValue.date).getTime()) / (1000 * 60 * 60 * 24)) : 30;
    
    const forecastPoints = [];
    const dailyChange = forecast.dailyChangeRate || 0;
    
    for (let i = 1; i <= Math.min(daysToTarget, 30); i++) {
      const futureDate = new Date(lastValue.date);
      futureDate.setDate(futureDate.getDate() + i);
      
      const projectedValue = lastValue.value + (dailyChange * i);
      
      forecastPoints.push({
        date: futureDate.toISOString().split('T')[0],
        projectedValue: Math.max(0, projectedValue),
        displayDate: futureDate.toLocaleDateString(),
        dayNumber: lastValue.dayNumber + i,
        isForecast: true
      });
    }
    
    return forecastPoints;
  };

  const getCombinedData = () => {
    const forecastData = generateForecastData();
    return [
      ...values.map(v => ({ ...v, projectedValue: null, isForecast: false })),
      ...forecastData.map(f => ({ ...f, value: null }))
    ];
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.7) return 'text-green-600';
    if (confidence >= 0.4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTargetAchievementStatus = () => {
    if (!forecast || !metric.targetValue) return null;
    
    const willMeet = forecast.willMeetTarget;
    const confidence = forecast.confidence;
    
    if (willMeet === null) return null;
    
    return {
      willMeet,
      confidence,
      message: willMeet 
        ? `🎯 Target likely to be achieved (${Math.round(confidence * 100)}% confidence)`
        : `⚠️ Target at risk (${Math.round(confidence * 100)}% confidence)`
    };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center text-red-600">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          {error}
        </div>
      </div>
    );
  }

  if (values.length < 2) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Forecast Analysis
        </h4>
        <div className="text-center text-gray-500 py-8">
          <Target className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium mb-2">Insufficient Data</p>
          <p>Add at least 2 data points to generate forecast</p>
        </div>
      </div>
    );
  }

  const combinedData = getCombinedData();
  const targetStatus = getTargetAchievementStatus();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Forecast Analysis
        </h4>
        {forecast && (
          <div className="flex items-center gap-4 text-sm">
            {getTrendIcon(forecast.trend)}
            <span className={`font-medium ${getConfidenceColor(forecast.confidence)}`}>
              {Math.round(forecast.confidence * 100)}% confidence
            </span>
          </div>
        )}
      </div>

      {/* Target Achievement Status */}
      {targetStatus && (
        <div className={`mb-4 p-3 rounded-lg border ${
          targetStatus.willMeet 
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <p className="font-medium">{targetStatus.message}</p>
          {forecast?.projectedValue && metric.targetValue && (
            <p className="text-sm mt-1">
              Projected: {forecast.projectedValue} {metric.unit} 
              (Target: {metric.targetValue} {metric.unit})
            </p>
          )}
        </div>
      )}

      {/* Chart */}
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={combinedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="displayDate" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              labelFormatter={(label) => `Date: ${label}`}
              formatter={(value, name) => [
                value ? `${value} ${metric.unit || ''}` : 'No data',
                name === 'value' ? 'Actual' : 'Projected'
              ]}
            />
            <Legend />
            
            {/* Historical Values */}
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#3B82F6" 
              strokeWidth={2}
              dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
              name="Actual Values"
              connectNulls={false}
            />
            
            {/* Forecast Line */}
            <Line 
              type="monotone" 
              dataKey="projectedValue" 
              stroke="#F59E0B" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#F59E0B', strokeWidth: 2, r: 3 }}
              name="Forecast"
              connectNulls={false}
            />
            
            {/* Target Line */}
            {metric.targetValue && (
              <ReferenceLine 
                y={metric.targetValue} 
                stroke="#10B981" 
                strokeDasharray="3 3"
                label="Target"
              />
            )}
            
            {/* Target Date Line */}
            {metric.targetDate && (
              <ReferenceLine 
                x={new Date(metric.targetDate).toLocaleDateString()} 
                stroke="#DC2626" 
                strokeDasharray="3 3"
                label="Target Date"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Forecast Details */}
      {forecast && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-gray-50 p-3 rounded">
            <div className="flex items-center gap-1 text-gray-600 mb-1">
              <TrendingUp className="w-4 h-4" />
              Trend
            </div>
            <div className="font-semibold capitalize">{forecast.trend}</div>
          </div>
          
          {forecast.dailyChangeRate && (
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-gray-600 mb-1">Daily Change</div>
              <div className="font-semibold">
                {forecast.dailyChangeRate > 0 ? '+' : ''}{forecast.dailyChangeRate} {metric.unit}/day
              </div>
            </div>
          )}
          
          {forecast.projectedValue && (
            <div className="bg-gray-50 p-3 rounded">
              <div className="flex items-center gap-1 text-gray-600 mb-1">
                <Target className="w-4 h-4" />
                Projected
              </div>
              <div className="font-semibold">{forecast.projectedValue} {metric.unit}</div>
            </div>
          )}
          
          {metric.targetDate && (
            <div className="bg-gray-50 p-3 rounded">
              <div className="flex items-center gap-1 text-gray-600 mb-1">
                <Calendar className="w-4 h-4" />
                Days Left
              </div>
              <div className="font-semibold">
                {Math.max(0, Math.ceil((new Date(metric.targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};